import { whom } from '../core/words.js';
import { TALKING } from './prompts.js';
import { optionsFor } from './procurement.js';
import { EVENT } from './events.js';
import { SHORTFALLS } from '../data/resources.js';
import { STAFF_BY_ID } from '../data/staff.js';
import { byId as departmentsById } from '../data/departments.js';

/**
 * Ask, trade, take — as a deal on the table, three things the key could do,
 * and what happens when you pick one.
 *
 * Procurement prices the options and resolves them; this is the conversation
 * side: which shortfall this person can do anything about, how each option is
 * offered, what the building hears when you help yourself, and what is said.
 * `releaseFor`, `releasedLine` and `applyShortfalls` are the stores' business
 * and are handed in.
 */
export function createDeals({ beos, procurement, standing, writeups, hud, bus, player, perception, autosave, applyShortfalls, releaseFor, releasedLine }) {
  /**
   * The deal on the table, if this person's department is holding something you
   * are short of.
   *
   * Only ever one shortfall at a time — the first unresolved one they own. Two
   * simultaneous negotiations with the same person is a menu, and the whole
   * design of this interaction is that it is fast enough to do in a doorway.
   */
  function dealFor(npc) {
    const active = beos.active;
    if (!active) return null;
    const person = STAFF_BY_ID[npc.data.id];
    if (!person || person.enforces) return null;

    const entry = (active.beo.shortfalls ?? []).find(
      (candidate) => !candidate.resolved && SHORTFALLS[candidate.id]?.owner === person.department
    );
    if (!entry) {
      const owns = (active.beo.shortfalls ?? []).some(
        (candidate) => SHORTFALLS[candidate.id]?.owner === person.department
      );
      return owns ? { closed: 'That one is covered.' } : null;
    }

    const shortfall = SHORTFALLS[entry.id];
    return {
      entry,
      person,
      title: `${person.name} · ${departmentsById[person.department]?.name ?? 'Guest'}`,
      shortfall: `${shortfall.label}${entry.amount ? ` ×${entry.amount}` : ''}. ${shortfall.detail}`,
      options: optionsFor(person, entry, { standing, strikes: writeups.count }),
    };
  }

  /**
   * What the game says when you help yourself.
   *
   * It no longer says SEEN, because at the moment you take something nobody has
   * decided anything. Somebody may have watched you and may be working up to
   * saying so, and the honest line is the one that tells you whether anybody was
   * facing your way — not what they are going to do about it.
   */
  function takeLine(person) {
    const said = person?.says?.caught;
    if (perception.watched) {
      // Whoever it belongs to gets the line, because being facing your way is the
      // only case where there is somebody to have said it.
      return said ? `Taken. ${person.name}: "${said}"` : 'Taken. Somebody was facing your way.';
    }
    return 'Taken. Nobody looked up.';
  }

  /**
   * Helping yourself is a thing that happens in the world, so it goes on the bus.
   *
   * Not to ask permission and not to find out whether anyone saw — the event is
   * emitted whether or not there is a soul in the building. Who noticed is
   * perception's business and what they do about it is somebody else's again.
   */
  function announceTake(entry) {
    bus.emit(EVENT.TAKE, {
      actor: 'player',
      kind: entry?.kind ?? null,
      detail: entry?.id ?? null,
      position: { ...player.position },
    });
  }

  /**
   * The three ways out of a shortfall, as three things the key could do.
   *
   * They used to be 1, 2 and 3 — three bindings that meant nothing anywhere else
   * in the game and were live only while a particular box happened to be open.
   * Now they are candidates like everything else: the first one the screen offers
   * and the other two a hold away, which is exactly what the fan is for. A
   * refused option is still offered, and still says why, because "the Chef does
   * not do favours" is information and a menu with one item on it teaches
   * nothing about why.
   */
  /** "ask Roz for the linen", "take the linen without asking Roz". */
  function phraseFor(kind, deal) {
    const row = SHORTFALLS[deal.entry.id];
    const wants = row.wants ?? row.label.toLowerCase();
    const who = whom(deal.person.name);
    return kind === 'take' ? `${wants} without asking ${who}` : `${who} for ${wants}`;
  }

  function offerDeal(deal) {
    if (!deal || deal.closed || !deal.options) return [];
    /*
     * The tap is the first one that is safe to take: an ask that would get you
     * reported, or one that already came to nothing today, or a door that is
     * shut, goes behind the hold. With the ask always the tap, a new starter
     * who read "hold A and pick Trade" pressed A once to talk and once more,
     * and was written up for it, twice.
     */
    // Safe first; then a take; then an ask that came to nothing; then one that
    // reports you; a shut door last.
    const risky = (option) =>
      !option.allowed
        ? 4
        : option.reportsYou
          ? 3
          : option.kind === 'ask' && flakedOn.has(deal.entry)
            ? 2
            : option.kind === 'take'
              ? 1
              : 0;
    return deal.options.map((option, index) => ({
      id: `deal-${option.kind}`,
      tier: TALKING,
      distance: risky(option) * 10 + index,
      verb: option.kind === 'trade' ? 'trade with' : option.kind,
      what: phraseFor(option.kind, deal),
      blocked: option.allowed ? null : option.reason,
      run: () => takeTheDeal(deal, option),
    }));
  }

  /**
   * Asks that came to nothing, by the event's own shortfall entry: the person
   * said yes and is not going to get to it, and asking again that day gets the
   * same shrug. Kept so the screen can stop telling you to ask (`main.js`).
   */
  const flakedOn = new WeakSet();

  function takeTheDeal(deal, option) {
    const result = procurement.resolve({
      person: deal.person,
      entry: deal.entry,
      kind: option.kind,
      beo: beos.active?.beo ?? null,
    });

    if (!result.ok) {
      if (result.failed) flakedOn.add(deal.entry);
      hud.say(`${deal.person.name}: ${result.reason}`, { tone: result.reported ? 'bad' : 'warn', seconds: 4 });
    } else if (result.via === 'ask') {
      const released = releaseFor(deal.entry);
      const said = deal.person.says?.ask;
      // One line, not two: the toast is one slot, and the second line of a
      // frame is the only one anybody ever saw. Where it was left is the part
      // that tells you what to do next, so it goes on the end.
      const where = releasedLine(released);
      hud.say(
        `${deal.person.name}${said ? `: "${said}"` : ' covers it.'} ${
          result.owed ? 'You owe them.' : 'No strings.'
        }${where ? ` ${where}` : ''}`,
        { tone: 'good', seconds: where ? 7 : 4 }
      );
    } else if (result.via === 'trade') {
      const said = deal.person.says?.trade ?? 'Do this first.';
      hud.say(`${deal.person.name}: "${said}" Go to ${whom(result.task.target.label)}.`, {
        tone: 'warn',
        seconds: 5,
      });
    } else {
      const taken = releaseFor(deal.entry);
      announceTake(deal.entry);
      const where = releasedLine(taken);
      hud.say(where ? `${takeLine(deal.person)} ${where}` : takeLine(deal.person), { tone: 'info', seconds: where ? 7 : 5 });
    }

    applyShortfalls();
    autosave.touch();
  }

  return {
    dealFor,
    offerDeal,
    takeTheDeal,
    takeLine,
    announceTake,
    /** Whether asking for this shortfall entry already came to nothing. */
    flaked: (entry) => flakedOn.has(entry),
  };
}
